import React from 'react';
import window from '@atlas/tool/box/browser/window';
import { notification } from 'antd';
import { createGlobalStyle, keyframes } from 'styled-components';
import { registerStylesheet } from '@atlas/uicorn/lib/System/StylesProvider';
import CrossIcon from '@atlas/uicorn/lib/Icon/CrossIcon';
import CheckIcon from '@atlas/uicorn/lib/Icon/CheckIcon';
import InfoIcon from '@atlas/uicorn/lib/Icon/InfoIcon';
import CloseIcon from '@atlas/uicorn/lib/Icon/CloseIcon';
import OminousIcon from '@atlas/uicorn/lib/Icon/OminousIcon';

const notify = {
    success(params: TNotifyParams) {
        openNotification({
            ...params,
            icon: <CheckIcon />,
            className: 'atlas-ant-notify atlas-ant-notify-success',
        });
    },
    info(params: TNotifyParams) {
        openNotification({
            ...params,
            icon: <InfoIcon />,
            className: 'atlas-ant-notify atlas-ant-notify-info',
        });
    },
    warning(params: TNotifyParams) {
        openNotification({
            ...params,
            icon: <OminousIcon />,
            className: 'atlas-ant-notify atlas-ant-notify-warning',
        });
    },
    error(
        params: Omit<TNotifyParams, 'message' | 'deduplicate'> &
            (
                | {
                      deduplicate: string | false;
                      message: React.ReactNode;
                  }
                | { deduplicate?: string | boolean; message: string }
            ),
    ) {
        openNotification({
            ...params,
            icon: <CloseIcon />,
            ...(typeof params.deduplicate === 'undefined' &&
                typeof params.message === 'string' && {
                    deduplicate: params.message,
                }),
            className: 'atlas-ant-notify atlas-ant-notify-error',
        });
    },
    backendException(e: any) {
        if (e?.response?.data?.visible && e.response.data.message) {
            this.error({ message: e.response.data.message });
        } else {
            this.error({ message: 'Server Error' });
        }
    },
};

export default notify;

// #region Helpers

notification.config({
    placement: 'bottomRight',
    duration: 5,
    closeIcon: <CrossIcon size={22} />,
});

const STACK_CLASS_NAME = 'stack';
const STACK_ANIMATION_CLASS_NAME = 'anim-stack';
const UNSTACK_ANIMATION_CLASS_NAME = 'anim-unstack';

const unstackingNotificationAnimation = keyframes`
    from {
        opacity: 0;
        transform: translateY(100%);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const stackingNotificationAnimation = keyframes`
    from {
        opacity: 1;
        transform: translateY(0);
    }
    to {
        opacity: 0;
        transform: translateY(100%);
    }
`;

const playAnimation = (className: string, callback?: () => void, duration = 150) => {
    const notifications = document.querySelectorAll('.ant-notification-notice');
    if (!notifications) return;

    notifications.forEach((notification) => {
        notification.classList.add(className);
    });

    setTimeout(() => {
        notifications.forEach((notification) => {
            notification.classList.remove(className);
        });
        callback && callback();
    }, duration);
};

const keysOfOpenedNotifications: Set<string> = new Set();

function updateNotificationStacked(stack = true) {
    const notificationsContainer = document.querySelector('.ant-notification');

    if (stack) {
        notificationsContainer?.classList.add(STACK_CLASS_NAME);
        return;
    }

    notificationsContainer?.classList.remove(STACK_CLASS_NAME);
}

let isUnstackedByHover = false;

function handleMouseEnter() {
    const notificationsContainer = document.querySelector('.ant-notification');
    if (!notificationsContainer || !notificationsContainer.classList.contains(STACK_CLASS_NAME)) return;

    isUnstackedByHover = true;
    updateNotificationStacked(false);

    playAnimation(UNSTACK_ANIMATION_CLASS_NAME);
}

function handleMouseLeave() {
    const notificationsContainer = document.querySelector('.ant-notification');
    if (!notificationsContainer || !isUnstackedByHover) return;

    isUnstackedByHover = false;

    playAnimation(STACK_ANIMATION_CLASS_NAME, () => updateNotificationStacked(true), 100);
}

function initHoverListener() {
    const notificationContainer = document.querySelector('.ant-notification') as HTMLElement;

    if (!notificationContainer || notificationContainer.onmouseenter || notificationContainer.onmouseleave) return;

    notificationContainer.onmouseenter = handleMouseEnter;
    notificationContainer.onmouseleave = handleMouseLeave;
}

function updateClassName(step: number) {
    const notifications = document.querySelectorAll('.ant-notification-notice');
    const notificationsContainer = document.querySelector('.ant-notification');
    if (!notifications.length) return;

    if (notifications.length + step >= 4 && step > 0) {
        if (!notificationsContainer?.classList.contains(STACK_CLASS_NAME)) {
            playAnimation(STACK_ANIMATION_CLASS_NAME, () => updateNotificationStacked(), 120);

            return;
        }

        updateNotificationStacked();
        return;
    }

    if (notificationsContainer?.classList?.contains(STACK_CLASS_NAME) && notifications.length + step !== 0) return;

    updateNotificationStacked(false);
}

function openNotification(params: TNotifyParams) {
    initHoverListener();
    updateClassName(+1);

    Object.assign(params, {
        onClose: () => {
            isUnstackedByHover = false;
            updateClassName(-1);
        },
    });

    if (params.deduplicate) {
        const key: string = params.deduplicate === true ? params.message : params.deduplicate;
        if (keysOfOpenedNotifications.has(key)) {
            notification.close(key);
        }

        keysOfOpenedNotifications.add(key);
        Object.assign(params, {
            key,
            onClose: () => {
                keysOfOpenedNotifications.delete(key);
                isUnstackedByHover = false;
                updateClassName(-1);
            },
        });
    }

    notification.open(params);
}

window && Object.assign(window, { atlasNotify: notify });

// #endregion

// #region Styles

const StackedNotificationFadeOut = keyframes`
    0% {
        opacity: 1;
        transform: translateY(0);
    }
    100% {
        opacity: 0;
        transform: translateY(30%);
    }
`;

const NotifyStylesheet = createGlobalStyle`
    .atlas-ant-notify {
        padding: 0.9rem 1.6rem;
        box-sizing: border-box;
        border: 1px solid ${(p) => p.theme.color.grey6};
        border-radius: 0.7rem;
        background: ${(p) => p.theme.color.grey0};
    }

    .ant-notification.stack .atlas-ant-notify {
        position: absolute;
        bottom: 0;
        right: 0;
        left: 0;
        transition: width 100ms, margin-right 100ms, bottom 100ms;
        box-shadow: none;

        &:is(:last-child) {
            box-shadow: 0 0.4rem 2.8rem 0.6rem ${(p) => p.theme.color.shadowLow};
        }

        &:is(:nth-last-child(2)) {
            bottom: 0.5rem;
            margin-right: 0.8rem;
            transition: width 500ms, margin-right 500ms, bottom 500ms;
            width: 36.6rem;
            max-width: calc(100vw - 2.4rem * 2.5);
        }

        &:is(:nth-last-child(n + 3)) {
            bottom: 1rem;
            margin-right: 1.5rem;
            width: 35rem;
            max-width: calc(100vw - 2.4rem * 3);
            transition: width 500ms, margin-right 500ms, bottom 500ms;
        }
    }

    .atlas-ant-notify.anim-unstack:not(:last-child) {
        animation: ${unstackingNotificationAnimation} 150ms;
    }

    .atlas-ant-notify.anim-stack:not(:last-child) {
        animation: ${stackingNotificationAnimation} 150ms;
    }

    .ant-notification.stack .ant-notification-fade-leave {
        position: absolute;
        bottom: 0;
        right: 0;
        left: 0;
        box-shadow: none;
    }

    .ant-notification.stack .ant-notification-fade-leave-active {
        animation-name: ${StackedNotificationFadeOut};
    }

    .ant-notification-notice-message {
        color: ${(p) => p.theme.color.grey9};
        margin-bottom: 0;
        font-weight: 500;
        font-size: 1.3rem;
        line-height: 2;
    }

    .ant-notification-notice-btn {
        margin: 0.4rem 0 0 0.4rem;
        display: inline-block;
        float: none;
    }

    .ant-notification-notice-description {
        font-size: 1.3rem;
        font-weight: 400;
        line-height: 1.6;
    }

    .ant-notification-notice-with-icon .ant-notification-notice-description {
        margin-left: 0;
    }

    .ant-notification-notice-with-icon .ant-notification-notice-message {
        font-size: 1.3rem;
        margin-left: 2.6rem;
        margin-bottom: 0;
    }

    .ant-notification-notice-icon {
        margin-left: 0;
        top: 0.7rem;

        svg {
            color: ${(p) => p.theme.color.grey9};
            width: 2.2rem;
            height: 2.2rem;
        }
    }

    .atlas-ant-notify-error .ant-notification-notice-icon svg,
    .atlas-ant-notify-error .ant-notification-notice-message
     {
        color: ${(p) => p.theme.color.red7};
    }

    .ant-notification-notice-close {
        top: 1rem;
        right: 1.6rem;

        svg {
            color: ${(p) => p.theme.color.grey8};
        }
    }
`;

registerStylesheet(NotifyStylesheet);

// #endregion

// #region Types

export type TNotifyParams = Pick<
    Parameters<typeof notification.open>[0],
    'icon' | 'duration' | 'className' | 'description' | 'btn' | 'onClose'
> &
    ({ deduplicate?: string | false; message: React.ReactNode } | { deduplicate?: boolean; message: string });

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, test, expect, vi, beforeEach, afterEach } = import.meta.vitest;
    describe('notify', () => {
        const notificationOpenMock = vi.spyOn(notification, 'open');

        beforeEach(() => {
            notificationOpenMock.mockReturnValue(undefined);
        });

        afterEach(() => {
            notificationOpenMock.mockReset();
        });

        test('calls AntD notification module', () => {
            notify.success({ message: 'Done!' });
            expect(notification.open).toBeCalledTimes(1);
            const successCall = notificationOpenMock.mock.calls[0];
            expect(successCall[0].className).toBe('atlas-ant-notify atlas-ant-notify-success');
            expect(successCall[0].message).toBe('Done!');

            notify.error({ message: 'Failed!' });
            expect(notification.open).toBeCalledTimes(2);
            const errorCall = notificationOpenMock.mock.calls[1];
            expect(errorCall[0].className).toBe('atlas-ant-notify atlas-ant-notify-error');
            expect(errorCall[0].message).toBe('Failed!');

            notify.info({ message: 'Listen!' });
            expect(notification.open).toBeCalledTimes(3);
            const infoCall = notificationOpenMock.mock.calls[2];
            expect(infoCall[0].className).toBe('atlas-ant-notify atlas-ant-notify-info');
            expect(infoCall[0].message).toBe('Listen!');

            notify.warning({ message: 'Careful!' });
            expect(notification.open).toBeCalledTimes(4);
            const warningCall = notificationOpenMock.mock.calls[3];
            expect(warningCall[0].className).toBe('atlas-ant-notify atlas-ant-notify-warning');
            expect(warningCall[0].message).toBe('Careful!');
        });
    });
}

// #endregion
